import { capacityLabel, parseReview, preciseLocation } from '../../lib/review-evidence';
import { pipelineCapacityDisplay } from '../../lib/pipeline-capacity';
import { meinersPipelineCapacity } from '../../lib/meiners-pipeline-capacity';
import React from 'react';
import type { BriefingData } from '../../lib/api';
import { findFeaturedPipeline, extractLandReadiness, isPriorityAmlOverlap, pipelineIsEffectivelyOnSite, ownerOwnsPipeline } from '../../lib/parcel-analysis';
import { isEntityOwner, formatAquiferName } from '../../lib/score-helpers';
import { buildLegendEntries } from './SchematicSVG';
import { SchematicMap } from './SchematicMap';
import { OperatorZoneCalloutV2 } from './OperatorZoneCalloutV2';
import { CandidateCarveOutPanelV2 } from './CandidateCarveOutPanelV2';
import { OperatorSupplyStoryV2 } from './OperatorSupplyStoryV2';
import { getParcelOverrides } from './parcel-overrides';
import { meanRange, pctRange } from '../briefing/slopeRange';

// ---------- formatting helpers ---------------------------------------------

const fmt = (n: number | null | undefined, digits = 0): string => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (digits === 0) return Math.round(v).toLocaleString();
  return v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};
const fmtMi = (n: number | null | undefined): string => (n == null ? '—' : `${Number(n).toFixed(2)} mi`);
const fmtAc = (n: number | null | undefined): string => (n == null ? '—' : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} ac`);
const shortId = (id: string) => id.replace(/-/g, '').slice(0, 8).toUpperCase();
const todayStr = new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

// ---------- gate computation ------------------------------------------------

// 'inquiry' is a caution-equivalent that reads as an open question rather than a
// warning — used where the honest status is "not yet answered". The badge style
// already exists (.v2-badge.inquiry, matching the DD checklist's inquiry tier).
type GateStatus = 'pass' | 'caution' | 'fail' | 'inquiry';
interface Gate { name: string; status: GateStatus; evidence: string }

function computeGates(data: BriefingData): Gate[] {
  const p = data.parcel;
  const gates: Gate[] = [];
  const gatesA1 = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1';
  const gatesMeiners = data.carveOutAggregate?.slug === 'meiners-ranch-la-salle';

  // Gas
  const heroPipe = findFeaturedPipeline(data.parcel, data.pipelines, data.carveOutAggregate?.slug);
  const gateCapacity = heroPipe ? pipelineCapacityDisplay(heroPipe) : null;
  const heroCap0 = gateCapacity?.capacityMmcf;
  const gateOnSite = pipelineIsEffectivelyOnSite(p, heroPipe);
  const gasEvidence = data.carveOutAggregate?.slug === 'crownpine-blk-194-a1'
    ? 'Energy Transfer 30″ Texoma line crosses the site’s western band (~0.52 mi on-premises; RRC-verified, in-service); gas availability and supply confirmed — firm transport and tap engineering remaining.'
    : gatesMeiners && heroPipe
    ? (() => {
        const fact = meinersPipelineCapacity(heroPipe);
        return `${titleCaseOperator(heroPipe.operator || heroPipe.name)}${heroPipe.diameter_inches ? ` ${heroPipe.diameter_inches}″` : ''} ${gateOnSite ? 'crosses parcel' : `at ${fmtPipeDist(heroPipe.distance_miles)}`} · ${fact.label}: ${fact.value}. ${fact.detail}`;
      })()
    : heroPipe
    ? `${titleCaseOperator(heroPipe.operator || heroPipe.name)}${heroPipe.diameter_inches ? ` ${heroPipe.diameter_inches}″` : ''} ${gateOnSite ? 'crosses parcel' : `at ${fmtPipeDist(heroPipe.distance_miles)}`}${heroCap0 ? ` · ${Math.round(heroCap0).toLocaleString()} MMcf/d ${gateCapacity!.label.toLowerCase()} (source-scoped)` : ''} · site delivery and firm transport unconfirmed`
    : 'No transmission pipeline within 10 mi radius.';
  // A1 gas gate passes on proximity to the RRC-verified 30″ ET Texoma transmission
  // line (within 5 mi) — explicitly NOT on a derived headroom MW.
  const gasStatus: GateStatus = gatesA1 || gatesMeiners
    ? (heroPipe && heroPipe.distance_miles <= 5 ? 'pass' : heroPipe ? 'caution' : 'fail')
    : heroPipe ? 'inquiry' : 'fail';
  gates.push({ name: 'Gas', status: gasStatus, evidence: gasEvidence });

  // Air
  const airPass = p.in_nonattainment === false;
  const airEvidence = p.in_nonattainment === true && p.nonattainment_areas?.length
    ? `Nonattainment for ${p.nonattainment_areas.map((a) => a.pollutant).join(', ')}.`
    : 'Attainment for all seven NAAQS · PSD pathway available.';
  gates.push({ name: 'Air', status: airPass ? 'pass' : 'caution', evidence: airEvidence });

  // Acreage — assembly-aware. When this parcel is part of a multi-parcel
  // single-owner carve-out (e.g. Paces Creek Woodlands' 4 parcels), the gate is
  // sized against the assembly total. Single-parcel carve-outs (Crown Pine
  // Lufkin Anchor = 1 parcel) read "1-parcel assembly" awkwardly, so the
  // assembly framing only fires when parcelCount > 1. Mirrors v1
  // BriefingContent.tsx:2666-2680.
  const aggAcresGate = data.carveOutAggregate?.totalAcreage ?? null;
  const aggParcelCountGate = data.carveOutAggregate?.parcelCount ?? 0;
  const useAggAcresGate = aggAcresGate != null && aggAcresGate > 0 && aggParcelCountGate > 1;
  const ac = useAggAcresGate ? Number(aggAcresGate) : Number(p.acreage ?? 0);
  const acreStatus: GateStatus = ac >= 1000 ? 'pass' : ac >= 500 ? 'caution' : 'fail';
  gates.push({
    name: 'Acreage',
    status: acreStatus,
    // A1: the gate is still decided on the analyzed core (`ac`, unchanged — the
    // threshold is 1,000 ac and the core clears it either way), but the evidence
    // line states the premises the reader is buying.
    evidence: data.premises
      ? `${fmtAc(data.premises.total_acres)} under one owner · ${aggParcelCountGate} parcels + a ${Math.round(data.premises.band_acres)}-ac band (1 in part).`
      : useAggAcresGate
      ? `${fmtAc(ac)} contiguous · ${aggParcelCountGate} parcels under one owner.`
      : `${fmtAc(ac)} contiguous · single ${titleCaseOperator((p.owner_name || 'owner').split(/[\/,]/)[0].trim())} holding.`,
  });

  // Flood — a NULL flood_zone means FEMA has published no digital NFHL for this
  // county, NOT that the parcel is unmapped-because-safe. Treating null as Zone X
  // printed an invented FEMA designation and passed the gate on absent evidence
  // (Frio County, 2026-08: DFIRM 48163C carries zero polygons, so every parcel in
  // the county rendered "FEMA Zone X (minimal risk)"). Unknown is a Watch, never a Pass.
  const fz = p.flood_zone;
  const floodKnown = !!fz;
  const floodPass = fz === 'X' || fz === 'D';
  gates.push({
    name: 'Flood',
    status: !floodKnown ? 'caution' : floodPass ? 'pass' : 'caution',
    evidence: !floodKnown
      ? 'Flood hazard undetermined — no FEMA digital flood mapping published for this county. Requires survey-grade topography and a drainage/hydrologic screen before LOI.'
      : floodPass
        ? `FEMA Zone ${fz} (minimal risk) · no 100-yr inundation on campus footprint.`
        : `FEMA Zone ${fz} — elevated flood exposure; mitigation required.`,
  });

  // Ownership
  const owned = !p.is_government_owner && !p.tax_delinquent;
  gates.push({
    name: 'Ownership',
    // A1: title has not been searched, so "Pass / clean title path" claimed a
    // conclusion nobody has reached. The title search is item 1 of the 60-day
    // plan on this same page.
    status: gatesA1 && owned ? 'inquiry' : owned ? 'pass' : 'caution',
    evidence: p.is_government_owner
      ? 'Government-owned parcel — non-standard acquisition path.'
      : p.tax_delinquent
        ? 'Tax-delinquent status — title review required before LOI.'
        : gatesA1
          ? `${ownerDisplay(p.owner_name) || 'Private ownership'} — title inquiry open.`
          : `${ownerDisplay(p.owner_name) || 'Private ownership'} — clean title path.`,
  });

  // Environmental — fail only when critical habitat is on-site (the actual
  // fatal-flaw signal). T&E range-map overlaps without critical habitat are
  // standard USFWS Section 7 informal-consultation procedure on any 2,000+
  // ac BTM siting, not a flaw — the range maps cover most of a state for
  // species like the red-cockaded woodpecker. Mark those as 'pass' with
  // informative evidence rather than 'caution' to avoid overstating risk.
  // `te_species_count` NULL means the T&E overlap was never computed for this
  // county, not that there are no overlaps. Collapsing null to 0 rendered
  // "No federal T&E range overlaps" as a Pass on parcels that in fact carry
  // listed-species ranges (Frio County, 2026-08: two overlaps present while the
  // column sat NULL). Not-assessed is a Watch, distinct from assessed-and-zero.
  const teAssessed = p.te_species_count != null;
  const te = p.te_species_count ?? 0;
  const inCH = p.in_critical_habitat === true;
  if (inCH) {
    gates.push({
      name: 'Environ.',
      status: 'fail',
      evidence: `Parcel overlaps USFWS critical habitat${p.critical_habitat_species ? ` for ${p.critical_habitat_species}` : ''} — formal Section 7 consultation required.`,
    });
  } else if (te > 0) {
    gates.push({
      name: 'Environ.',
      status: 'pass',
      evidence: gatesA1
        ? `No critical habitat on site. ${te} federal T&E range-map overlap${te === 1 ? '' : 's'}${p.te_species_list ? ` (${p.te_species_list.split(',').slice(0, 3).join(',')}${p.te_species_list.split(',').length > 3 ? '…' : ''})` : ''} — range overlap alone triggers nothing; Section 7 applies only via a federal nexus (realistically the §404 action, if the north drain is ruled jurisdictional). Habitat assessment scopes it in DD.`
        : `No critical habitat on site. ${te} federal T&E range-map overlap${te === 1 ? '' : 's'}${p.te_species_list ? ` (${p.te_species_list.split(',').slice(0, 3).join(',')}${p.te_species_list.split(',').length > 3 ? '…' : ''})` : ''} — standard Section 7 informal consultation, not a flaw.`,
    });
  } else if (teAssessed) {
    gates.push({
      name: 'Environ.',
      status: 'pass',
      evidence: 'No federal T&E range overlaps · no critical habitat on site.',
    });
  } else {
    gates.push({
      name: 'Environ.',
      status: 'caution',
      evidence: 'Federal T&E range overlap not yet assessed for this county — USFWS IPaC screen required before LOI. No critical habitat recorded on site.',
    });
  }

  return gates;
}

// ---------- section heading ------------------------------------------------

const Heading: React.FC<{ tag: string; title: string; meta?: string }> = ({ tag, title, meta }) => (
  <div className="v2-section-head">
    <span className="v2-num-tag">{tag}</span>
    <h2>{title}</h2>
    {meta && <span className="v2-meta">{meta}</span>}
  </div>
);

// ---------- main content ----------------------------------------------------

export const BriefingV2Content: React.FC<{ data: BriefingData }> = ({ data: rawData }) => {
  // Per-parcel curated overrides (Paces Creek for now). When a parcel has
  // overrides, augment data.pipelines with any injected entries so the
  // briefing's pipeline-dependent blocks (thesis, schematic, infra) all
  // pick them up. Parity with v1 (BriefingContent.tsx PACES_PARCEL_IDS).
  // Also pre-process the auto-detected Gulf South 16" Magasco lateral:
  // surface the Magasco subsystem name (the API rolls all Gulf South
  // subsystems under registry name "Gulf South Pipeline Co"; for Paces
  // the operator-confirmable lateral is "Magasco → Call Junction"), and
  // force distance_miles=0 because pipeline-systems measures from the rep
  // parcel centroid and 0.83 mi does not reflect that the line crosses
  // the 4-parcel assembly polygon.
  const overrides = getParcelOverrides(rawData.parcel.id);
  const preprocessedPipelines = overrides
    ? (rawData.pipelines || []).map(pp => {
        const isGsMagasco = pp.operator
          && /gulf south/i.test(pp.operator)
          && pp.diameter_inches === 16
          && Number(pp.distance_miles) < 5;
        if (!isGsMagasco) return pp;
        return {
          ...pp,
          system_name: 'Magasco → Call Junction Lateral',
          name: pp.name || 'MAGASCO C.S. TO CALL JUNCTION',
          distance_miles: 0,
        };
      })
    : (rawData.pipelines || []);
  const data: BriefingData = overrides?.injectPipelines.length
    ? { ...rawData, pipelines: [...preprocessedPipelines, ...overrides.injectPipelines] }
    : overrides
      ? { ...rawData, pipelines: preprocessedPipelines }
      : rawData;
  // v2 reads slope from parcel.avg_slope_pct (the rep parcel's coarse 10 m sample). A briefing
  // with a slope file carries the 3–5 m figures on the carve-out aggregate (3 m in weighted*,
  // 5 m in slopeGrid5m; .claude/rules/terrain-slope.md): swap them in so v2 matches v1.
  const agg = data.carveOutAggregate;
  if (agg?.slopeGrid5m && agg.weightedAvgSlopePct != null && data.parcel) {
    data.parcel = { ...data.parcel, avg_slope_pct: agg.weightedAvgSlopePct, pct_under_5pct: agg.weightedPctUnder5Pct };
  }
  const isA1 = agg?.slug === 'crownpine-blk-194-a1';
  const a1SlopeRange = agg?.slopeGrid5m && agg.weightedAvgSlopePct != null && agg.weightedPctUnder5Pct != null
    ? { avg: meanRange(agg.weightedAvgSlopePct, agg.slopeGrid5m.avgSlopePct), under5: pctRange(agg.weightedPctUnder5Pct, agg.slopeGrid5m.pctUnder5Pct) }
    : null;
  const p = data.parcel;
  const isMeiners = data.carveOutAggregate?.slug === 'meiners-ranch-la-salle';
  const gates = computeGates(data);
  // Paces flood override: replace generic evidence with carve-out-level math
  // (8.4% of assembly polygon falls in FEMA Zone A; the ~91.6% outside is the
  // mitigation evidence). Hardcoded until carve-out flood_pct field lands.
  if (overrides && data.carveOutAggregate?.totalAcreage) {
    const fg = gates.find(g => g.name === 'Flood');
    if (fg && fg.status !== 'pass') {
      const total = Number(data.carveOutAggregate.totalAcreage);
      const floodPct = 8.4;
      const usable = Math.round(total * (1 - floodPct / 100));
      fg.evidence = `FEMA Zone ${p.flood_zone || 'A'} — ${floodPct.toFixed(1)}% of the ${Math.round(total).toLocaleString()}-ac assembly; ~${usable.toLocaleString()} ac (${(100 - floodPct).toFixed(1)}%) outside the flood polygon supports campus siting.`;
    }
  }
  // Crown Pine A1: the flood gate's generic "no 100-yr inundation" line is not quite
  // true — ~11.3 ac of Zone A ride one drainage corridor. FEMA's own Zone X term
  // ("minimal flood hazard") is the honest label.
  if (isA1) {
    const fg = gates.find(g => g.name === 'Flood');
    if (fg) fg.evidence = 'Minimal flood hazard (FEMA Zone X) across ~99.5% of the assembly; ~11.3 ac of Zone A confined to one drainage corridor, routed around by the pad plan.';
  }
  const gatesPassCount = gates.filter((g) => g.status === 'pass').length;
  // 'inquiry' is an open question, not a fatal flaw — it must not knock a site
  // out of the no-fatal-flaws verdict. Only the A1 Ownership gate can produce it
  // (computeGates gates it on gatesA1), so for every other briefing this count
  // equals gatesPassCount and the verdict branch below is unchanged.
  const gatesClearCount = gates.filter((g) => g.status === 'pass' || g.status === 'inquiry').length;
  const inquiryGates = gates.filter((g) => g.status === 'inquiry');

  // AOI carve-out aggregate. Multi-parcel single-owner assemblies (Paces Creek
  // Woodlands = 4 parcels at 2,356 ac per listing) re-frame headline acreage and prose as
  // "X-parcel assembly". Single-parcel carve-outs (Crown Pine Lufkin Anchor = 1
  // parcel) read awkwardly with "1-parcel assembly", so the assembly framing
  // only fires when parcelCount > 1. The headline acreage value is the assembly
  // total either way; for parcelCount=1 that's identical to parcel.acreage.
  const aggAcres = data.carveOutAggregate?.totalAcreage ?? null;
  const aggParcelCount = data.carveOutAggregate?.parcelCount ?? 0;
  const useAggAcres = aggAcres != null && aggAcres > 0 && aggParcelCount > 1;
  const headlineAcres = aggAcres != null && aggAcres > 0 ? Number(aggAcres) : (p.acreage ? Number(p.acreage) : null);

  // Operator-zone peak supply remains in the operator's native Bcf/d units.
  // It is system/project context and does not establish parcel delivery.
  const aoiPeakBcfd = data.gasSupplyZone?.peakBcfd != null
    ? Number(data.gasSupplyZone.peakBcfd)
    : null;
  // Schematic overlay is only useful when the carve-out covers MORE than the
  // briefing parcel (e.g. Paces Creek's 4 parcels). For single-parcel carve-outs
  // (Crown Pine = 1 parcel), the polygon would double-trace the briefing
  // parcel's own boundary, so suppress. Mirrors v1 BriefingMap.tsx:618-624.
  const firstCarveOut = data.carveOuts?.[0] ?? null;
  const carveOutPrimary = firstCarveOut && (firstCarveOut.parcelIds?.length ?? 0) > 1
    ? firstCarveOut
    : null;

  const heroPipe = findFeaturedPipeline(p, data.pipelines, data.carveOutAggregate?.slug);
  const heroCapacity = heroPipe ? pipelineCapacityDisplay(heroPipe) : null;
  const heroCap = heroCapacity?.capacityMmcf;
  const meinersHeroCapacity = isMeiners && heroPipe ? meinersPipelineCapacity(heroPipe) : null;

  // Hero pipeline "on site" — single source of truth. Shared helper covers:
  //   (a) geometry ≤ 0.05 mi (~264 ft), (b) parcel owner is the pipeline operator,
  //   (c) WV DEP permit override (pipeline name matches parcel's pipeline_available_name).
  // Every "on site" render surface on this page reads from heroOnSite so the page
  // never contradicts itself (e.g. masthead saying "0.51 mi" while KeyNumbers says
  // "on site" for the same Goff Connector Pipeline).
  const heroOnSite = pipelineIsEffectivelyOnSite(p, heroPipe);
  const landReadiness = extractLandReadiness(p.site_notes);

  // Landowner water disclosure — surfaces in 4 places (Water block, Kill Criteria,
  // Decision Point, Epistemic Pending) so the page doesn't self-contradict. When
  // the owner has directly disclosed water on site, replace the generic "$15–30K
  // hydrogeological survey / 30 days" item with "operator materials + site visit /
  // 1–2 weeks" (pattern mirrors parcel-analysis.ts:800-807 in computeResolveItems).
  const waterDisclosure = (p as any).structured_intel?.landowner_disclosures?.water_on_site;
  const hasWaterDisclosure = !!waterDisclosure?.display_note;

  // IXP / peering — only call something an "internet exchange" when it actually
  // hosts ≥5 networks. Anything below that is a regional ISP POP (e.g. Citynet
  // Bridgeport, 1 network, 4.9 mi from Goff). Fall back to nearest_colo_* which
  // the backend already pre-filters to networks_count ≥ 5 at briefing.js:388.
  const ixpNets = p.nearest_ixp_networks != null ? Number(p.nearest_ixp_networks) : null;
  const ixpOk = !!(p.nearest_ixp_name && p.nearest_ixp_miles != null && ixpNets != null && ixpNets >= 5);
  const coloOk = !ixpOk && !!((p as any).nearest_colo_facility && (p as any).nearest_colo_miles != null);

  // Secondary / redundant pipelines (different operators)
  const redundantOps: Array<{ operator: string; distance_miles: number; diameter: number | null; capacity: number | null; util: number | null; maop: number | null }> = [];
  {
    const seen = new Set<string>([heroPipe?.operator || '']);
    for (const pipe of data.pipelines) {
      const op = pipe.operator || pipe.name;
      if (seen.has(op)) continue;
      seen.add(op);
      redundantOps.push({
        operator: op,
        distance_miles: pipe.distance_miles,
        diameter: pipe.diameter_inches,
        capacity: pipelineCapacityDisplay(pipe).capacityMmcf,
        util: pipe.utilization_pct,
        maop: pipe.maop_psig,
      });
      if (redundantOps.length >= 2) break;
    }
  }

  // Regulatory precedent: largest gas-fired plant within 30 mi (validates air permitting)
  const gasPrecedent = (data.powerInfra?.powerPlants || [])
    .filter((pp) => pp.capacity_mw && pp.distance_miles <= 30 && /natural.gas/i.test(pp.fuel_source || ''))
    .sort((a, b) => (Number(b.capacity_mw) || 0) - (Number(a.capacity_mw) || 0))[0];

  // Industry precedent: nearest DC within 50 mi (validates market thesis)
  const statusRank: Record<string, number> = { operational: 0, under_construction: 1, announced: 2 };
  const dcPrecedent = (data.nearbyDataCenters || [])
    .filter((dc) => dc.distance_miles <= 50 && dc.state === p.state && dc.review?.comparison_eligible !== false && !['cancelled', 'on_hold', 'unknown'].includes(dc.review?.operating_status || ''))
    .sort((a, b) => {
      const rankDiff = (statusRank[a.status] ?? 3) - (statusRank[b.status] ?? 3);
      if (rankDiff !== 0) return rankDiff;
      const precisionDiff = Number(preciseLocation(parseReview(b.review))) - Number(preciseLocation(parseReview(a.review)));
      return precisionDiff || a.distance_miles - b.distance_miles;
    })[0];
  const dcEvidence = parseReview(dcPrecedent?.review);

  const hasPrecedent = !!gasPrecedent || !!dcPrecedent || !!overrides?.precedents.length;

  // Regional new-build precedent: curated scalar from structured_intel.power_expansion_mw.
  // When present, the fourth top-strip tile surfaces gas-generation announced at OTHER
  // nearby sites — the thesis-validating precedent figure an IC cares about. When absent,
  // the tile falls back to the Phase-2 ceiling (pipeline rated sum) below.
  const regionalNewBuildMw = Number((p as any).structured_intel?.power_expansion_mw ?? NaN);
  const hasRegionalNewBuild = Number.isFinite(regionalNewBuildMw) && regionalNewBuildMw > 0;

  // data.premises is Crown Pine A1 only — passing it swaps legend row A from the
  // (nonexistent) "tap · 1.35 mi" to the crossing framing. Undefined elsewhere.
  const legendEntries = buildLegendEntries(
    p, data.pipelines, data.nearestFeatures, data.gasInfrastructure?.compressors?.[0] || null, heroOnSite,
    data.premises ? { crossingMi: data.premises.band_crossing_mi } : null,
  );

  return (
    <div className="v2-sheet">
      {/* ============================================================= */}
      {/* DATELINE                                                        */}
      {/* ============================================================= */}
      <header className="v2-dateline">
        <div className="v2-seq">
          {(p.parcel_id_local || 'UNNAMED-PARCEL').toUpperCase()} &nbsp;·&nbsp;
          <strong>{shortId(p.id)}</strong> &nbsp;·&nbsp;
          MEMO
        </div>
        <div className="v2-right">
          {todayStr}
        </div>
      </header>

      {/* ============================================================= */}
      {/* MASTHEAD                                                        */}
      {/* ============================================================= */}
      <section className="v2-masthead">
        <div className="v2-eyebrow">
          {data.gasSupplyZone?.operatorName ? (
            <>
              {data.gasSupplyZone.operatorName.toUpperCase()} SUPPLY ZONE
              <span className="v2-eyebrow-sep">·</span>
              {p.county.toUpperCase()}, {p.state}
              {(data.gasSupplyZone.regionLabel || data.gasSupplyZone.assetName) ? (() => {
                const r = (data.gasSupplyZone.regionLabel || '').trim();
                const a = (data.gasSupplyZone.assetName || '').trim();
                const base = a && r && a.toLowerCase().startsWith(r.toLowerCase())
                  ? a
                  : [r, a].filter(Boolean).join(' ');
                // Append "Shale" when the asset name names recognized shale plays.
                const isShalePlay = /haynesville|bossier|eagle ford|marcellus|utica|bakken|wolfcamp|barnett|spraberry/i.test(base);
                const combined = isShalePlay ? `${base} Shale` : base;
                return (
                  <>
                    <span className="v2-eyebrow-sep">·</span>
                    {combined.toUpperCase()}
                  </>
                );
              })() : null}
            </>
          ) : (
            <>SITE INTELLIGENCE BRIEF<span className="v2-eyebrow-sep">·</span>ATWATER ENERGY<span className="v2-eyebrow-sep">·</span>PRE-FEASIBILITY</>
          )}
        </div>
        <h1>
          {firstCarveOut?.name
            ? firstCarveOut.name.replace(/\s+—\s+.*$/, '')
            : <>{p.county}, {stateName(p.state)}</>}
        </h1>
        <p className="v2-subhead">
          {data.gasSupplyZone ? (
            <>
              A {headlineAcres != null ? (data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? Number(headlineAcres).toLocaleString() : Math.round(Number(headlineAcres)).toLocaleString()) : '—'}-acre single-owner block inside the {data.gasSupplyZone.operatorName} supply zone.
            </>
          ) : (
            <>
              {/* A1: the H1 names the carve-out ("Angelina A1 · Hwy 69 tract"), so the lede
                  must size the assembly, not the single briefing parcel. Without this the
                  zone-null path prints 624.03 ac against the 2,443.62 ac the acreage gate
                  and thesis both state on the same page. */}
              {isA1 && (data.premises || headlineAcres != null) ? fmtAc(data.premises?.total_acres ?? headlineAcres) : fmtAc(p.acreage)} under {p.owner_name ? `“${ownerDisplay(p.owner_name, 60)}”` : 'private ownership'}
              {heroPipe && isA1 ? (
                // A1 wording is hard-gated: the memo names the operator "Energy Transfer"
                // (the data field reads "ENERGY TRANSFER COMPANY"), and the lede now states
                // the crossing rather than the standoff distance. No data field is renamed;
                // non-A1 briefings keep the branch below verbatim.
                <>, with a <em>{`${heroPipe.diameter_inches ? `${heroPipe.diameter_inches}″ ` : ''}Energy Transfer`}</em>{` line crossing its western band.`}</>
              ) : heroPipe ? <>, sitting on a <em>{heroPipe.diameter_inches ? `${heroPipe.diameter_inches}″` : ''} {titleCaseOperator(heroPipe.operator || heroPipe.name)}</em> line {heroOnSite ? 'that crosses the parcel directly' : <>at {fmtPipeDist(heroPipe.distance_miles)}</>} {heroCap != null ? `with ${Math.round(heroCap).toLocaleString()} MMcf/d of published system capacity` : 'with no parcel-level delivery capacity established'}.</> : '.'}
            </>
          )}
        </p>

        <dl className="v2-qa">
          <dt>Question this brief answers</dt>
          <dd>
            {isA1 ? (
              <>Is this a credible path to stand up a latency-insensitive compute campus under Texas&rsquo;s established
              standard-permit pathway, with gas from an in-service 30&Prime; transmission line crossing the site?</>
            ) : isMeiners ? (
              <>Is this a credible path to a latency-insensitive compute campus with nearby gas infrastructure whose total capacity, site delivery and firm transport are verified with the operators?</>
            ) : (
              <>Is this a credible path to stand up a latency-insensitive compute campus under a standard air permit,
              behind pipeline capacity that is currently underutilized?</>
            )}
          </dd>
          <dt>Current answer</dt>
          <dd>
            {(() => {
              const openGates = gates.filter(g => g.status !== 'pass');
              const isPaces = !!overrides;
              const headlineAcLabel = headlineAcres != null
                ? `${Number(headlineAcres).toLocaleString(undefined, { maximumFractionDigits: data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? 2 : 0 })}-ac assembly`
                : 'assembly';
              if (gatesClearCount === gates.length) {
                if (isA1) {
                  // An open inquiry is named rather than hidden — the reader can
                  // see 5 Pass + 1 Inquiry in Sec. 03 and the headline must
                  // account for the difference.
                  if (inquiryGates.length > 0) {
                    return <><strong>Yes</strong> — no fatal flaws identified across the {gates.length} screening gates on public data; one gate ({inquiryGates[0].name}) carries an open title inquiry. The diligence checklist tracks the open inquiries; each is priced or resolvable below.</>;
                  }
                  return <><strong>Yes</strong> — no fatal flaws identified across the {gates.length} screening gates on public data. The diligence checklist tracks the open inquiries; each is priced or resolvable below.</>;
                }
                return <><strong>Yes</strong>, with the open items below priced or resolvable. All {gates.length} fatal-flaw gates clear on public data.</>;
              }
              if (isPaces && openGates.length === 1 && openGates[0].name === 'Flood' && headlineAcres) {
                const floodPct = 8.4;
                const usable = Math.round(Number(headlineAcres) * (1 - floodPct / 100));
                return <>
                  <strong>{gatesPassCount} of {gates.length} gates clear</strong> — the one open gate (flood zone) affects <strong>{floodPct.toFixed(1)}% of the {headlineAcLabel}</strong>, leaving <strong>~{usable.toLocaleString()} ac outside the flood polygon</strong> for siting.
                </>;
              }
              return <>
                <strong>{gatesPassCount} of {gates.length} gates clear</strong> — see Sec. 03 for the gate(s) requiring remediation before an LOI.
              </>;
            })()}
          </dd>
        </dl>
      </section>

      {/* ============================================================= */}
      {/* HEADLINE NUMBERS (hero + two tiles)                             */}
      {/* ============================================================= */}
      <div className="v2-numbers">
        <div className="v2-num v2-num-hero">
          {data.gasSupplyZone && aoiPeakBcfd != null ? (
            !!overrides ? (
            <>
              <div className="v2-label">Power Story</div>
              <div className="v2-value v2-value-stack">
                <div className="v2-value-line">
                  <span className="v2-value-stage">250 MW</span>
                  <span className="v2-value-when"> · Jun 2027</span>
                </div>
                <div className="v2-value-line">
                  <span className="v2-value-arrow">&rarr;</span>
                  <span className="v2-value-stage"> {aoiPeakBcfd.toFixed(2)} Bcf/d</span>
                  <span className="v2-value-when"> · peak 2031</span>
                </div>
              </div>
              <div className="v2-delta v2-delta-prose">
                Phase 1 project context from {data.gasSupplyZone.operatorName.replace(/\s+Energy\s*$/i, '')}'s gathering system; the {aoiPeakBcfd.toFixed(2)} Bcf/d peak is gated on the operator's {data.gasSupplyZone.inventoryYears ? `${data.gasSupplyZone.inventoryYears}-year` : '30-year'} drilling inventory across 100k ac of Haynesville/Bossier leasehold. It does not establish parcel delivery or firm transport. <strong>{isA1 ? 'NAAQS attainment — TCEQ EGU Standard Permit path for early phases; PSD only at full buildout.' : 'NAAQS attainment, standard PSD.'}</strong>
              </div>
            </>
            ) : (
            <>
              <div className="v2-label">Power Story</div>
              <div className="v2-value">
                {aoiPeakBcfd.toFixed(2)}
                <span className="v2-unit"> Bcf/d</span>
              </div>
              <div className="v2-delta">
                {data.gasSupplyZone.operatorName} peak supply{data.gasSupplyZone.assetName ? ` · ${data.gasSupplyZone.assetName}` : ''}
              </div>
            </>
            )
          ) : isA1 ? (
            // A1 carries the physical crossing fact and keeps delivery capacity open.
            <>
              <div className="v2-label">Gas line on site</div>
              <div className="v2-value">{data.premises ? <>{data.premises.band_crossing_mi.toFixed(2)}<span className="v2-unit"> mi crossing</span></> : '—'}</div>
              <div className="v2-delta">
                Energy Transfer 30&Prime; Texoma crosses the site&rsquo;s western band — in-service; gas availability and supply confirmed, firm transport and tap engineering remaining.
              </div>
            </>
          ) : isMeiners ? (
            <>
              <div className="v2-label">{meinersHeroCapacity?.label || 'Total pipeline capacity'}</div>
              <div className="v2-value">{meinersHeroCapacity?.value || 'Unconfirmed'}</div>
              <div className="v2-delta">
                {heroPipe ? `${titleCaseOperator(heroPipe.operator || heroPipe.name)} · ${heroPipe.diameter_inches ? `${heroPipe.diameter_inches}″ · ` : ''}${meinersHeroCapacity?.detail || 'Confirm total capacity and delivery terms with the operator.'}` : 'No pipeline within 10 mi'}
              </div>
            </>
          ) : (
            <>
              <div className="v2-label">Published pipeline capacity</div>
              <div className="v2-value">{heroCap != null ? Math.round(heroCap).toLocaleString() : 'Unconfirmed'}<span className="v2-unit">{heroCap != null ? ' MMcf/d' : ''}</span></div>
              <div className="v2-delta">
                {heroPipe ? `${titleCaseOperator(heroPipe.operator || heroPipe.name)} · ${heroPipe.diameter_inches ? `${heroPipe.diameter_inches}″ · ` : ''}${heroCap != null ? `${Math.round(heroCap).toLocaleString()} MMcf/d ${heroCapacity!.label.toLowerCase()}` : 'native capacity not published'}; parcel delivery and firm transport unconfirmed.` : 'No pipeline within 10 mi'}
              </div>
            </>
          )}
        </div>
        <div className="v2-num">
          {(() => {
            const colo = (p as any).nearest_colo_facility;
            const coloMi = (p as any).nearest_colo_miles;
            const coloOk = !!(colo && coloMi != null);
            const ixpMi = ixpOk ? Number(p.nearest_ixp_miles) : (coloOk ? Number(coloMi) : null);
            const ixpName = ixpOk ? p.nearest_ixp_name : (coloOk ? colo : null);
            // 0.022 ms/mi RTT — Houston→Asheville WonderNetwork ping calibration,
            // matches the figure displayed in the archived East Texas zone briefing
            // (114 mi → 2.5 ms RTT) for cross-document consistency. Houston→Dallas
            // gives 0.024 as a corroborating bound; both sit inside a 0.3 ms window.
            const rttMs = ixpMi != null ? (ixpMi * 0.022) : null;
            return ixpMi != null && rttMs != null ? (
              <>
                <div className="v2-label">Latency to peering</div>
                <div className="v2-value">~{rttMs.toFixed(1)}<span className="v2-unit"> ms RTT</span></div>
                <div className="v2-delta">
                  {ixpName} · {fmtMi(ixpMi)}{ixpOk && ixpNets ? ` · ${ixpNets} carriers` : ''}
                  {' '}— a latency-insensitive workload absorbs this comfortably.
                </div>
              </>
            ) : (
              <>
                <div className="v2-label">Latency to peering</div>
                <div className="v2-value">—</div>
                <div className="v2-delta">No qualified IXP / peering facility within reach.</div>
              </>
            );
          })()}
        </div>
        <div className="v2-num">
          {isA1 ? (
            // A1: keep the physical line and downstream scheduling context without
            // turning diameter into a delivery-capacity claim.
            <>
              <div className="v2-label">Gas supply</div>
              <div className="v2-value">Confirmed</div>
              <div className="v2-delta">
                Gas availability and supply confirmed — delivery expected in tranches as project phases build out; firm transport and tap engineering define final terms. Screening derivation retained in the analytical view.
              </div>
            </>
          ) : isMeiners ? (
            <>
              <div className="v2-label">Firm deliverability</div>
              <div className="v2-value">Unconfirmed</div>
              <div className="v2-delta">Published total pipeline capacity does not establish a receipt point, pressure, local delivery or firm transportation to the block.</div>
            </>
          ) : hasRegionalNewBuild ? (
            <>
              <div className="v2-label"><strong style={{ color: 'var(--accent)', fontWeight: 700 }}>PRECEDENT</strong> · Nearby new-build</div>
              <div className="v2-value">{regionalNewBuildMw >= 1000 ? fmt(regionalNewBuildMw / 1000, 1) : fmt(regionalNewBuildMw)}<span className="v2-unit">{regionalNewBuildMw >= 1000 ? ' GW' : ' MW'}</span></div>
              <div className="v2-delta">
                Gas generation announced at other sites within 30 mi
              </div>
            </>
          ) : (
            <>
              <div className="v2-label">Pipeline deliverability</div>
              <div className="v2-value">Unconfirmed</div>
              <div className="v2-delta">
                Published system capacity and geometry do not establish local delivery or firm transportation.
                {data.gasSupplyZone
                  ? <> Public-midstream optionality on top of the {data.gasSupplyZone.operatorName} firm-supply path.</>
                  : null}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ============================================================= */}
      {/* ZONE CONTEXT (AOI parcels only)                                 */}
      {/* ============================================================= */}
      {/* Conditionally rendered when the parcel sits inside an operator  */}
      {/* gas-supply zone (currently revenant-east-tx only). Frames the   */}
      {/* gas story upstream of the per-parcel infra blocks below so the  */}
      {/* operator commitment is the headline, not pipeline_available_mw. */}
      {/* Goff / Reeves / non-AOI parcels render nothing here.            */}
      {data.gasSupplyZone && (
        <section className="v2-zone-context">
          <OperatorZoneCalloutV2 zone={data.gasSupplyZone} />
          {!overrides?.suppressCarveOutBox && data.carveOutAggregate?.slug !== 'crownpine-blk-194-a1' && (data.carveOuts || []).map((co) => (
            <CandidateCarveOutPanelV2
              key={co.id}
              carveOut={co}
              representativeParcelLabel={
                co.representativeParcelId === p.id ? 'this parcel (representative)' : null
              }
            />
          ))}
          <OperatorSupplyStoryV2 zone={data.gasSupplyZone} suppressPrecedent={overrides?.suppressZonePrecedent} />
        </section>
      )}

      {/* ============================================================= */}
      {/* THESIS                                                          */}
      {/* ============================================================= */}
      <section>
        <Heading tag="Sec. 01" title="Thesis" meta={`${p.county} · ${stateName(p.state)}`} />
        <p className="v2-prose">
          {heroPipe ? (
            <>
              {(() => {
                const onSite = heroOnSite;
                // Strip trailing "Pipeline" from pipeName so we don't end up saying
                // "Goff Connector Pipeline pipeline runs through this parcel".
                const rawPipeName = heroPipe.name && !/fullstream|holdings|llc|lp|inc|company|operating|corp/i.test(heroPipe.name) ? titleCaseOperator(heroPipe.name) : null;
                const pipeName = rawPipeName ? rawPipeName.replace(/\s+pipeline\s*$/i, '') : null;
                const opName = titleCaseOperator(heroPipe.operator || heroPipe.name);
                const subjectPhrase = onSite
                  ? (pipeName
                      ? <>The <strong>{pipeName}</strong> pipeline runs <strong>through this parcel</strong>{opName && opName !== pipeName ? <>, operated by <strong>{opName}</strong>,</> : null}</>
                      : <><strong>{opName}</strong>’s pipeline runs <strong>through this parcel</strong></>)
                  : <><strong>{opName}</strong> runs within <strong>{fmtPipeDist(heroPipe.distance_miles)}</strong></>;
                // When on-site, lean on what we can actually source: the parcel's
                // pipeline_available_local_source (WV DEP permit #) when present, else
                // the softer "tap grantable on-parcel" phrasing (matches KeyNumbers delta).
                const permitId = p.pipeline_available_local_source?.startsWith('wvdep_permit')
                  ? p.pipeline_available_local_source.replace('wvdep_permit_', '')
                  : null;
                const ownerOps = ownerOwnsPipeline(p.owner_name, heroPipe);
                const utilPhrase = isMeiners
                  ? null
                  : onSite
                  ? (permitId
                      ? <> — <strong>owner-operated pipeline with a published permit reference</strong> (WV DEP {permitId}); delivery capacity remains to be confirmed</>
                      : ownerOps
                        ? <> — <strong>owner-operated pipeline, tap grantability requires confirmation</strong></>
                        : <> — <strong>geometry only; tap, receipt point, and firm transport subject to a standard operator interconnect study</strong></>)
                  : null;
                return <>{subjectPhrase}{utilPhrase}</>;
              })()} —
              {/* The memo carries no capacity number on A1 — the operator study
                  defines it. The screening derivation stays in the analytical
                  view's gas-panel footnote and the claims audit. */}
              {isMeiners && meinersHeroCapacity
                ? ` ${meinersHeroCapacity.label}: ${meinersHeroCapacity.value}; ${meinersHeroCapacity.detail}`
                : !isA1 && heroCap ? ` a ${Math.round(heroCap).toLocaleString()} MMcf/d ${heroCapacity!.label.toLowerCase()}` : ''}
              {heroPipe.diameter_inches ? `${isA1 ? ' a' : ','} ${heroPipe.diameter_inches}-inch` : ''}
              {heroPipe.maop_psig ? `, ${Number(heroPipe.maop_psig).toLocaleString()} psig` : ''} header
              .
              {(() => {
                const comp = data.gasInfrastructure?.compressors?.[0];
                return comp && comp.distance_miles <= 1 ? ` ${comp.name || 'Compressor station'} (${comp.horsepower ? `${Number(comp.horsepower).toLocaleString()} HP` : ''}${comp.num_units ? `, ${comp.num_units} units` : ''}) at ${Number(comp.distance_miles).toFixed(2)} mi provides on-site compression.` : '';
              })()}
              {' '}{data.premises
                ? <>Energy Transfer&rsquo;s Texoma 30&Prime; crosses the site&rsquo;s western band (~{data.premises.band_crossing_mi.toFixed(2)} mi on-premises) and runs within 1.35 mi of the analyzed core. The premises total <strong>{fmtAc(data.premises.total_acres)}</strong> under {p.owner_name ? <em>“{ownerDisplay(p.owner_name, 50)}”</em> : 'a single holder'} — a {fmtAc(data.premises.core_acres)} analyzed core of {data.carveOutAggregate!.parcelCount} contiguous parcels plus the {fmtAc(data.premises.band_acres)} western band the gas line crosses (every terrain, flood and water figure on this page is measured on the core)</>
                : useAggAcres
                ? <>The {data.carveOutAggregate!.parcelCount}-parcel assembly totals <strong>{fmtAc(headlineAcres)} contiguous</strong> under {p.owner_name ? <em>“{ownerDisplay(p.owner_name, 50)}”</em> : 'a single holder'}</>
                : <>The parcel is <strong>{fmtAc(p.acreage)} contiguous</strong> under {p.owner_name ? <em>“{ownerDisplay(p.owner_name, 50)}”</em> : 'a single holder'}</>
              }
              {/* A1: this clause is derived from `is_absentee`, a tax-mailing-address
                  datum. It renders nothing today only because the flag happens to be
                  true — if a CAD refresh flips it, A1 would start calling an
                  institutional timberland owner "locally-based". Gate it, don't rely
                  on the data. The expression is kept in place (not removed) so the
                  SSR text-node markers on Goff and Paces stay byte-identical. */}
              {!isA1 && p.is_absentee === false
                ? (isEntityOwner(p.owner_name) ? ' (locally-based entity)' : ' (resident owner)')
                : ''}.
              {' '}
              {p.in_nonattainment === false ? (
                isA1
                  ? <>County is in <em>attainment</em> for all seven NAAQS — early phases fit TCEQ&rsquo;s registration-based EGU Standard Permit (the instrument Crusoe&rsquo;s Longhorn campus used baseload), with case-by-case PSD reserved for full buildout.</>
                  : <>County is in <em>attainment</em> for all seven NAAQS — the air-quality path is a standard PSD permit, not a major-source fight.</>
              ) : (
                <>County is in <em>nonattainment</em>{p.nonattainment_areas?.length ? ` for ${p.nonattainment_areas.map((a) => a.pollutant).join(', ')}` : ''} — a major-source permitting path applies.</>
              )}
            </>
          ) : (
            <>
              No transmission pipeline lies within 10 miles of this parcel. The BTM thesis does not apply here —
              this brief exists to record what is present on site and why the site is parked, not to advance it.
            </>
          )}
        </p>

        <p className="v2-prose">
          {slopeConstraintText(p.avg_slope_pct, p.pct_under_5pct, landReadiness, a1SlopeRange)}
          {hasWaterDisclosure
            ? <> BTM air-cooled auxiliary water demand is de&nbsp;minimis (~1,000 GPD, zero evaporative makeup); <strong>owner has confirmed water on site</strong> (specs pending — operator materials + site visit to document source, yield, and quality).</>
            : p.nearest_water_miles != null
              ? <> BTM air-cooled auxiliary water demand is de&nbsp;minimis (~1,000 GPD, zero evaporative makeup); nearest recorded source is <strong>{fmtMi(p.nearest_water_miles)}</strong> from the parcel.</>
              : ''}
          {' '}
          {overrides?.gasRedundancy ?? (redundantOps.length > 0 ? (
            <>Redundant gas supply exists in the form of {redundantOps.map((r, i) => <span key={i}><strong>{titleCaseOperator(r.operator)}</strong> at {fmtPipeDist(r.distance_miles)}{r.diameter ? ` (${r.diameter}″)` : ''}{i < redundantOps.length - 1 ? ', ' : ''}</span>)} — multi-operator redundancy inside a 10-mile radius is uncommon and a quiet premium for supply-chain robustness.</>
          ) : '')}
        </p>
      </section>

      {/* ============================================================= */}
      {/* PRECEDENT                                                       */}
      {/* ============================================================= */}
      {hasPrecedent && (
        <section>
          <Heading tag="Sec. 02" title="Precedent" meta={overrides?.precedents.length ? overrides.precedentsMeta : gasPrecedent && dcPrecedent ? 'Regulatory + industry' : gasPrecedent ? 'Regulatory' : 'Industry'} />

          {overrides?.precedentCompare ? (
            <div className="v2-pc-compare">
              {overrides.precedentCompare.intro && (
                <p className="v2-prose v2-pc-compare-intro">{overrides.precedentCompare.intro}</p>
              )}
              <table className="v2-pc-compare-table">
                <colgroup>
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Capacity</th>
                    <th>Acres</th>
                    <th>Gas / power</th>
                    <th>Air permit</th>
                    <th>Land</th>
                  </tr>
                </thead>
                <tbody>
                  {overrides.precedentCompare.rows.map((r, i) => (
                    <tr
                      key={i}
                      className={[
                        r.emphasis ? 'v2-pc-row-emphasis' : '',
                        r.inAoi ? 'v2-pc-row-inaoi' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <td>
                        <div className="v2-pc-row-label">{r.label}</div>
                        {r.sub && <div className="v2-pc-row-sub">{r.sub}</div>}
                        {r.context && <div className="v2-pc-row-context">{r.context}</div>}
                      </td>
                      <td>{r.capacity}</td>
                      <td>{r.acres}</td>
                      <td>{r.gas}</td>
                      <td>{r.permit}</td>
                      <td>{r.land}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {overrides.precedentCompare.takeaway && (
                <p className="v2-prose v2-pc-compare-takeaway">{overrides.precedentCompare.takeaway}</p>
              )}
            </div>
          ) : overrides?.precedents.length ? (
            <div className="v2-precedent-stack">
              {overrides.precedents.map((cp, i) => (
                <div className={`v2-pc-card${cp.highlight ? ' highlight' : ''}`} key={i}>
                  <div className="v2-pc-eyebrow">
                    {cp.highlight ? 'In-AOI precedent · the working playbook' : 'TX BTM precedent'}
                  </div>
                  <div className="v2-pc-title">{cp.name}</div>
                  <div className="v2-pc-stat-row">
                    <div className="v2-pc-stat"><span className="v2-pc-stat-val">{cp.capacity}</span><span className="v2-pc-stat-lbl">Capacity</span></div>
                    <div className="v2-pc-stat"><span className="v2-pc-stat-val">{cp.distance}</span><span className="v2-pc-stat-lbl">Distance</span></div>
                    <div className="v2-pc-stat"><span className="v2-pc-stat-val v2-pc-stat-small">{cp.location}</span><span className="v2-pc-stat-lbl">Location</span></div>
                    <div className="v2-pc-stat"><span className="v2-pc-stat-val v2-pc-stat-small">{cp.status}</span><span className="v2-pc-stat-lbl">Status</span></div>
                  </div>
                  <div className="v2-pc-operator">{cp.operator}</div>
                  <p className="v2-pc-detail">{cp.detail}</p>
                </div>
              ))}
            </div>
          ) : (

          <div style={{ display: 'grid', gridTemplateColumns: gasPrecedent && dcPrecedent ? '1fr 1fr' : '1fr', columnGap: 48, rowGap: 32 }}>

            {/* Regulatory precedent — gas-fired plant */}
            {gasPrecedent && (
              <div>
                <div style={{ fontFamily: 'var(--v2-mono)', fontSize: 'var(--ts-micro)', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-muted)', marginBottom: 12 }}>
                  Regulatory precedent
                </div>
                <div className="v2-precedent-data">
                  <div className="v2-pd-label">Name</div>
                  <div className="v2-pd-val">{gasPrecedent.name}</div>
                  <div className="v2-pd-label">Distance</div>
                  <div className="v2-pd-val">{Number(gasPrecedent.distance_miles).toFixed(1)}&nbsp;<em>mi</em></div>
                  <div className="v2-pd-label">Nameplate</div>
                  <div className="v2-pd-val">{fmt(gasPrecedent.capacity_mw)} <em>MW</em></div>
                  <div className="v2-pd-label">Operator</div>
                  <div className="v2-pd-val v2-pd-small">{gasPrecedent.operator || '—'}</div>
                  <div className="v2-pd-label">Status</div>
                  <div className="v2-pd-val v2-pd-small"><em>{(gasPrecedent.status || '—').replace(/_/g, ' ')}</em></div>
                </div>
                <p className="v2-prose" style={{ marginTop: 16, fontSize: 'var(--ts-small)' }}>
                  Validates that the air-quality regulator has accepted a gas-fired combustion source in this airshed.
                  Pipeline operator appetite for interconnect and firm transport is proven at this corridor.
                </p>
              </div>
            )}

            {/* Industry precedent — data center */}
            {dcPrecedent && (
              <div>
                <div style={{ fontFamily: 'var(--v2-mono)', fontSize: 'var(--ts-micro)', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-muted)', marginBottom: 12 }}>
                  Industry precedent
                </div>
                <div className="v2-precedent-data">
                  <div className="v2-pd-label">Name</div>
                  <div className="v2-pd-val">{dcPrecedent.name}</div>
                  <div className="v2-pd-label">Distance</div>
                  <div className="v2-pd-val">{preciseLocation(dcEvidence) ? Number(dcPrecedent.distance_miles).toFixed(1) : `~${Math.round(dcPrecedent.distance_miles)}`}&nbsp;<em>mi{preciseLocation(dcEvidence) ? '' : ' · approximate'}</em></div>
                  {dcPrecedent.capacity_mw && (
                    <>
                      <div className="v2-pd-label">{capacityLabel(dcEvidence)}</div>
                      <div className="v2-pd-val">{fmt(dcPrecedent.capacity_mw)} <em>MW</em></div>
                    </>
                  )}
                  <div className="v2-pd-label">Operator</div>
                  <div className="v2-pd-val v2-pd-small">{dcPrecedent.operator || '—'}</div>
                  <div className="v2-pd-label">Status</div>
                  <div className="v2-pd-val v2-pd-small" style={{ color: dcPrecedent.status === 'operational' ? 'var(--pass)' : dcPrecedent.status === 'under_construction' ? 'var(--caution)' : 'var(--ink-muted)' }}>
                    <em>{(dcPrecedent.status || '—').replace(/_/g, ' ')}</em>
                    {dcPrecedent.year_announced ? <> · announced {dcPrecedent.year_announced}</> : ''}
                    {dcPrecedent.year_opened ? <> · reported opening {dcPrecedent.year_opened}</> : ''}
                    {dcEvidence.status_as_of ? <> · as of {dcEvidence.status_as_of}</> : ' · current status unverified'}
                  </div>
                  <div className="v2-pd-label">Location</div>
                  <div className="v2-pd-val v2-pd-small">{[dcPrecedent.city, dcPrecedent.county, dcPrecedent.state].filter(Boolean).join(', ')}</div>
                </div>
                <p className="v2-prose" style={{ marginTop: 16, fontSize: 'var(--ts-small)' }}>
                  Nearby data-center records provide regional context. Confirm current operating status and site identity before relying on this comparison.
                  {dcPrecedent.status === 'under_construction' ? ' Currently under construction — track for operational confirmation.' : ''}
                  {dcPrecedent.status === 'announced' ? ' Announced but not yet under construction — track progression.' : ''}
                  {dcPrecedent.nearest_pipeline_name ? ` Mapped near ${dcPrecedent.nearest_pipeline_name}; proximity does not establish pipeline service.` : ''}
                  {dcEvidence.sources?.[0] && <> <a href={dcEvidence.sources[0].url} target="_blank" rel="noopener noreferrer">{dcEvidence.sources[0].title}</a>{dcEvidence.reviewed_at ? ` · reviewed ${dcEvidence.reviewed_at}` : ''}.</>}
                </p>
              </div>
            )}

          </div>
          )}
        </section>
      )}

      {/* ============================================================= */}
      {/* FATAL-FLAW GATES                                                */}
      {/* ============================================================= */}
      <section>
        <Heading tag={hasPrecedent ? 'Sec. 03' : 'Sec. 02'} title="Fatal-flaw screen" meta="Public data only · no site confirmation" />
        <div className="v2-gates">
          {gates.map((gate) => (
            <div className="v2-gate" key={gate.name}>
              <div className="v2-gate-name">{gate.name}</div>
              <div className="v2-gate-evidence">{gate.evidence}</div>
              <span className={`v2-badge ${gate.status}`}>{gate.status === 'pass' ? 'Pass' : gate.status === 'caution' ? 'Watch' : gate.status === 'inquiry' ? 'Inquiry' : 'Fail'}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================= */}
      {/* KILL CRITERIA                                                   */}
      {/* ============================================================= */}
      <section>
        <Heading tag={hasPrecedent ? 'Sec. 04' : 'Sec. 03'} title="Kill criteria" meta="What would stop this · cost to find out" />
        <table className="v2-kill">
          <thead>
            <tr>
              <th>Kill criterion</th>
              <th>Test</th>
              <th>Cost to resolve</th>
              <th>Timeline</th>
            </tr>
          </thead>
          <tbody>
            {p.avg_slope_pct != null && Number(p.avg_slope_pct) > 8 && (
              <tr>
                <td>
                  Grading cost exceeds $/MW budget at {Number(p.avg_slope_pct).toFixed(1)}% avg slope{p.pct_under_5pct != null ? ` (only ${Number(p.pct_under_5pct).toFixed(1)}% of parcel < 5% grade)` : ''}
                  {landReadiness && <div style={{ fontSize: 'var(--ts-caption)', color: 'var(--pass)', marginTop: 4, fontStyle: 'italic' }}>Mitigated: {landReadiness}</div>}
                </td>
                <td>Geotech survey + cut/fill earthwork bid.</td>
                <td className="v2-cost">&lt; $50K</td>
                <td className="v2-when">45 days</td>
              </tr>
            )}
            {/* The $0.5–1.1M row was generic and priced a lateral A1 does not
                need — the line crosses the proposed premises. A1 gets the
                interconnect scope with cost pending tap engineering; every
                other briefing keeps the priced row byte-for-byte. */}
            {heroPipe && !heroOnSite && !data.gasSupplyZone && (isA1 ? (
              <tr>
                <td>Firm transport and tap engineering not yet executed</td>
                <td>Firm transport and tap engineering with {titleCaseOperator(heroPipe.operator || heroPipe.name)} — gas availability and supply confirmed.</td>
                <td className="v2-cost">Interconnect facilities and routing scope · Cost TBD pending tap engineering</td>
                <td className="v2-when">6–18-month critical-path allowance from acceptance of a complete request</td>
              </tr>
            ) : (
              <tr>
                <td>Pipeline tap not grantable at {fmtPipeDist(heroPipe.distance_miles)}</td>
                <td>Operator interconnect study with {titleCaseOperator(heroPipe.operator || heroPipe.name)} — formal request within 14 days.</td>
                <td className="v2-cost">$0.5–1.1M</td>
                <td className="v2-when">6–18 mo</td>
              </tr>
            ))}
            {(p.water_service_area !== true) && (
              hasWaterDisclosure ? (
                <tr>
                  <td>
                    Landowner water specifications not yet documented
                    <div style={{ fontSize: 'var(--ts-caption)', color: 'var(--pass)', marginTop: 4, fontStyle: 'italic' }}>Disclosed on site: {waterDisclosure.display_note}</div>
                  </td>
                  <td>Operator materials + site visit to confirm source, yield, and quality.</td>
                  <td className="v2-cost">—</td>
                  <td className="v2-when">1–2 weeks</td>
                </tr>
              ) : (
                <tr>
                  <td>Auxiliary water below 1,000 GPD sustainable yield</td>
                  <td>Hydrogeological survey OR commercial-haul economics for air-cooled auxiliary needs.</td>
                  <td className="v2-cost">$15–30K</td>
                  <td className="v2-when">30 days</td>
                </tr>
              )
            )}
            {(p.te_species_count != null && p.te_species_count > 0) && (
              <tr>
                <td>{isA1 ? 'T&E habitat found AND a federal nexus attaches (e.g., §404 action) → Section 7 consultation' : 'T&E species consultation triggers full Section 7'}</td>
                <td>{isA1 ? <>Habitat assessment during DD scopes it — range-map overlap alone triggers nothing{p.te_species_list ? ` (${p.te_species_list.split(',').slice(0, 2).join(',')})` : ''}.</> : <>USFWS informal consultation scoping; habitat assessment{p.te_species_list ? ` (${p.te_species_list.split(',').slice(0, 2).join(',')})` : ''}.</>}</td>
                <td className="v2-cost">$25–60K</td>
                <td className="v2-when">60–120 days</td>
              </tr>
            )}
            {p.in_mine_subsidence_zone === true && (
              <tr>
                <td>Underground mine subsidence risk on campus footprint</td>
                <td>Subsurface geotech + WV DEP-AML records review; buildable-footprint delineation.</td>
                <td className="v2-cost">$35–75K</td>
                <td className="v2-when">60 days</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* ============================================================= */}
      {/* SITE SCHEMATIC                                                  */}
      {/* ============================================================= */}
      <section>
        <Heading tag={hasPrecedent ? 'Sec. 05' : 'Sec. 04'} title="Site schematic" meta="Parcel &amp; infrastructure corridor" />

        <div className="v2-schematic">
          <SchematicMap parcel={p} pipelines={data.pipelines} mapLayers={data.mapLayers} nearestFeatures={data.nearestFeatures} carveOut={carveOutPrimary} carveOutSlug={data.carveOutAggregate?.slug} />

          <div className="v2-legend">
            {/* Whole rows branch at the element level, not an inline ternary
                inside a text node: an expression spliced into the single-child
                spans changes React's SSR markers and breaks Goff/Paces
                byte-identity. Rows with two distance annotations drop them onto
                their own line so the label keeps the full column width. */}
            {legendEntries.map((entry) => (entry.dist2 ? (
              <div className="v2-pin-row v2-pin-row-stacked" key={entry.mark}>
                <span className="v2-pin-mark" style={entry.color ? { color: entry.color } : undefined}>{entry.mark}</span>
                <span className="v2-pin-label">{entry.label}</span>
                <span className="v2-pin-dist"><span>{entry.dist}</span><span>{entry.dist2}</span></span>
              </div>
            ) : (
              <div className="v2-pin-row" key={entry.mark}>
                <span className="v2-pin-mark" style={entry.color ? { color: entry.color } : undefined}>{entry.mark}</span>
                <span className="v2-pin-label">{entry.label}</span>
                <span className="v2-pin-dist">{entry.dist}</span>
              </div>
            )))}
            <div className="v2-cap">
              Geometry from public record · boundary simplified for schematic use · pipelines from {p.state === 'TX' ? 'TX RRC / ' : p.pipeline_available_local_source?.includes('wvdep') ? 'WV DEP TAGIS / ' : ''}HIFLD / EIA · substations &amp; rail from HIFLD · north up.
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* INFRASTRUCTURE                                                  */}
      {/* ============================================================= */}
      <section>
        <Heading tag={hasPrecedent ? 'Sec. 06' : 'Sec. 05'} title="Infrastructure at a glance" meta="Power · gas · fiber · water · logistics" />

        <div className="v2-infra-grid">

          {/* Power */}
          {(() => {
            const sub2Kv = p.second_substation_kv != null ? Number(p.second_substation_kv) : null;
            const sub2IsTransmission = sub2Kv != null && sub2Kv >= 69;
            const bulkSubs = (data.powerInfra as any)?.bulkSubstations || [];
            const txLines = data.powerInfra?.transmissionLines || [];
            const backboneSub = bulkSubs[0];
            const backboneLine = txLines.find((l: any) => l.voltage_kv != null && Number(l.voltage_kv) >= 345);
            const backbone = backboneSub || backboneLine;
            const backboneIsClose = backbone && Number(backbone.distance_miles) <= 15;
            return (
              <div className="v2-infra-block">
                <div className="v2-infra-head">
                  <h3>Power</h3>
                  <span className="v2-tag amber">Bridging only</span>
                </div>
                <p>
                  {p.nearest_substation_kv ? <><strong>{p.nearest_substation_kv} kV</strong> substation{p.nearest_substation_name && !/^UNKNOWN/i.test(p.nearest_substation_name) ? ` (${p.nearest_substation_name})` : ''} at <strong>{fmtMi(p.nearest_substation_miles)}</strong>{p.nearest_substation_lines ? `, ${p.nearest_substation_lines}-line feeder` : ''}.</> : <>Nearest substation at <strong>{fmtMi(p.nearest_substation_miles)}</strong>.</>}
                  {p.second_substation_miles && sub2IsTransmission ? <> Secondary substation at <strong>{fmtMi(p.second_substation_miles)}</strong> ({sub2Kv} kV) for redundancy.</> : ''}
                  {' '}Sufficient for auxiliary and construction load via will-serve letter; full grid service is not the BTM campus's primary path.
                  {backboneIsClose ? <> Bulk transmission backbone within reach: <strong>{backboneSub ? (backboneSub.name && !/^UNKNOWN\d+/i.test(backboneSub.name) ? backboneSub.name : `${Number(backboneSub.voltage_kv).toLocaleString()} kV substation`) : (backboneLine!.name || `${Number(backboneLine!.voltage_kv).toLocaleString()} kV line`)}</strong> at <strong>{fmtMi(Number(backbone.distance_miles))}</strong> ({Number((backboneSub || backboneLine!).voltage_kv).toLocaleString()} kV) — optional path for future wholesale export.</> : ''}
                </p>
              </div>
            );
          })()}

          {/* Gas */}
          <div className="v2-infra-block">
            <div className="v2-infra-head">
              <h3>Gas</h3>
              <span className="v2-tag">Strength</span>
            </div>
            <p>
              {heroPipe ? (
                <>
                  <strong>{titleCaseOperator(heroPipe.operator || heroPipe.name)}</strong>{heroPipe.diameter_inches ? ` ${heroPipe.diameter_inches}″` : ''} {heroOnSite ? 'crosses parcel' : `at ${fmtPipeDist(heroPipe.distance_miles)}`}
                  {isMeiners && meinersHeroCapacity
                    ? <>, <strong>{meinersHeroCapacity.label}: {meinersHeroCapacity.value}</strong>. {meinersHeroCapacity.detail}</>
                    : !isA1 && heroCap ? `, ${Math.round(heroCap).toLocaleString()} MMcf/d ${heroCapacity!.label.toLowerCase()}` : ''}
                  {heroPipe.maop_psig ? `, MAOP ${Number(heroPipe.maop_psig).toLocaleString()} psig` : ''}
                  . Published capacity is system context; local delivery and firm transport require operator confirmation.
                  {heroOnSite && !ownerOwnsPipeline(p.owner_name, heroPipe) ? <> Geometry signal only — pipeline crosses the parcel but does not imply an existing tap or receipt point; a standard operator interconnect study establishes the delivery point.</> : ''}
                  {heroOnSite && ownerOwnsPipeline(p.owner_name, heroPipe) ? <> Pipeline is operated by the landowner — tap is grantable on-parcel without a third-party interconnect study.</> : ''}
                  {redundantOps.map((r, i) => (
                    <span key={i}> <strong>{titleCaseOperator(r.operator)}</strong>{r.diameter ? ` ${r.diameter}″` : ''} at {fmtPipeDist(r.distance_miles)}{r.maop ? ` (${Number(r.maop).toLocaleString()} psig)` : ''} offers redundancy.</span>
                  ))}
                  {(() => {
                    const comp = data.gasInfrastructure?.compressors?.[0];
                    return comp && comp.distance_miles <= 1 ? <> {comp.name || 'Compressor station'} ({comp.horsepower ? `${Number(comp.horsepower).toLocaleString()} HP` : ''}{comp.num_units ? `, ${comp.num_units} units` : ''}) at {fmtMi(comp.distance_miles)} provides on-site compression.</> : '';
                  })()}
                  {data.pipelineSafety ? <> PHMSA reports <strong>{data.pipelineSafety.total_incidents}</strong> incident{data.pipelineSafety.total_incidents === 1 ? '' : 's'} within 10 mi since {data.pipelineSafety.years_covered.split('–')[0] || '2014'}.</> : ''}
                  {/* isA1 gate: rewording approved for A1; Paces (protected) also renders
                      this sentence — the non-A1 branch must reproduce the original JSX
                      exactly (same text/expression boundaries) to stay byte-identical
                      under SSR. */}
                  {data.gasSupplyZone?.corridorTakeaway?.totalBcfd != null ? (isA1
                    ? <> Public pipeline corridor within reach: <strong>{data.gasSupplyZone.corridorTakeaway.totalBcfd.toFixed(1)} Bcf/d across {data.gasSupplyZone.corridorTakeaway.pipelineCount} lines</strong> — regional infrastructure context outside {data.gasSupplyZone.operatorName.replace(/\s+Energy\s*$/i, '')}'s firm-transport path, not supply demonstrated accessible to this site.</>
                    : <> Public pipeline corridor within reach: <strong>{data.gasSupplyZone.corridorTakeaway.totalBcfd.toFixed(1)} Bcf/d across {data.gasSupplyZone.corridorTakeaway.pipelineCount} lines</strong> — backup supply outside {data.gasSupplyZone.operatorName.replace(/\s+Energy\s*$/i, '')}'s firm-transport path.</>) : ''}
                </>
              ) : 'No transmission pipeline within 10 mi of the parcel.'}
            </p>
          </div>

          {/* Fiber */}
          <div className="v2-infra-block">
            <div className="v2-infra-head">
              <h3>Fiber</h3>
              <span className="v2-tag amber">Carrier inquiry</span>
            </div>
            <p>
              {isA1
                ? <>FiberLight enterprise backbone at <strong>0.57 mi</strong> from the assembly boundary is the nearest mapped carrier-route lead.</>
                : isMeiners
                  ? <>Whole-block boundary leads: <strong>FiberLight at ~3.1 mi</strong> and <strong>Zayo at ~6.3 mi</strong>.</>
                  : p.nearest_carrier_fiber_name && p.nearest_carrier_fiber_miles != null
                    ? <><strong>{p.nearest_carrier_fiber_name}</strong> at <strong>{fmtMi(p.nearest_carrier_fiber_miles)}</strong> from the parcel boundary is the nearest mapped carrier-route lead.</>
                    : p.carrier_fiber_nearest && p.carrier_fiber_nearest.length > 0
                      ? <>Mapped carrier-route leads from the representative parcel centroid: {p.carrier_fiber_nearest.slice(0, 2).map((cf, i) => <span key={i}><strong>{cf.carrier}</strong> at <strong>{Number(cf.nearest_mi).toFixed(1)} mi</strong>{i < Math.min(p.carrier_fiber_nearest!.length, 2) - 1 ? ', ' : ''}</span>)}.</>
                      : <>Enterprise carrier route mapping remains an open item.</>}
              {' '}Serviceability, capacity, construction scope, SLA, and route diversity remain pending direct carrier confirmation.
              {ixpOk
                ? <> Nearest internet exchange <strong>{p.nearest_ixp_name}</strong> at <strong>{fmtMi(p.nearest_ixp_miles)}</strong> ({ixpNets} networks) — ~{((Number(p.nearest_ixp_miles) || 0) * 0.022).toFixed(1)}&nbsp;ms RTT.</>
                : coloOk
                  ? <> Nearest peering <strong>{(p as any).nearest_colo_facility}</strong> at <strong>{fmtMi((p as any).nearest_colo_miles)}</strong>{(p as any).nearest_colo_networks ? ` (${(p as any).nearest_colo_networks} carriers)` : ''} — ~{((Number((p as any).nearest_colo_miles) || 0) * 0.022).toFixed(1)}&nbsp;ms RTT.</>
                  : ''}
            </p>
          </div>

          {/* Water */}
          <div className="v2-infra-block">
            <div className="v2-infra-head">
              <h3>Water</h3>
              <span className={`v2-tag ${hasWaterDisclosure ? '' : 'brick'}`}>{hasWaterDisclosure ? 'Landowner-verified' : 'Open item'}</span>
            </div>
            <p>
              {hasWaterDisclosure ? (
                <>
                  <strong>Landowner has confirmed water on site.</strong> {waterDisclosure.display_note}
                  {waterDisclosure.source ? <> Disclosed by <em>{waterDisclosure.source}</em>{waterDisclosure.reported_date ? ` (${waterDisclosure.reported_date})` : ''}.</> : ''}
                  {Array.isArray(waterDisclosure.outstanding_items) && waterDisclosure.outstanding_items.length > 0
                    ? <> Outstanding to confirm: {waterDisclosure.outstanding_items.join('; ')}.</>
                    : ''}
                  {' '}Air-cooled BTM heat rejection keeps auxiliary demand at ~1,000 GPD (fire suppression, domestic, equipment cooling) — resolvable in 1–2 weeks via operator materials plus a site visit; no hydrogeological survey required.
                </>
              ) : (
                <>
                  {p.water_service_area === true
                    ? <>Inside municipal service area. Auxiliary water demand is de&nbsp;minimis at BTM air-cooled scale (~1,000 GPD, zero evaporative makeup).</>
                    : <>No municipal service. Nearest recorded water point at <strong>{fmtMi(p.nearest_water_miles)}</strong>.</>
                  }
                  {p.water_sourcing_tier ? <> Water sourcing tier: <strong>{p.water_sourcing_tier.replace(/_/g, ' ')}</strong>.</> : ''}
                  {p.aquifer_name ? <> Overlies <em>{formatAquiferName(p.aquifer_name)}</em>.</> : ''}
                  {' '}Air-cooled heat rejection eliminates evaporative water demand; the ~1,000 GPD auxiliary requirement (fire suppression, domestic, equipment cooling) is solvable by single-well yield testing or a standing commercial-haul contract.
                </>
              )}
            </p>
          </div>

          {/* Logistics */}
          <div className="v2-infra-block">
            <div className="v2-infra-head">
              <h3>Logistics</h3>
              <span className="v2-tag">Strength</span>
            </div>
            <p>
              {overrides?.logistics ?? (
                <>
                  {p.nearest_railroad_miles != null ? <><strong>{humanizeRail(p.nearest_railroad_owner)}</strong> at {fmtMi(p.nearest_railroad_miles)}. </> : ''}
                  {p.nearest_highway_name && p.nearest_highway_miles != null ? <><strong>{p.nearest_highway_name}</strong> at {fmtMi(p.nearest_highway_miles)}. </> : ''}
                  {p.nearest_commercial_airport_name && p.nearest_commercial_airport_miles != null ? <><strong>{titleCase(p.nearest_commercial_airport_name)} Airport</strong>{isA1 ? ' (GGG, scheduled commercial)' : data.nearestFeatures?.airport?.faa_id ? ` (${data.nearestFeatures.airport.faa_id})` : ''} at {fmtMi(p.nearest_commercial_airport_miles)}. </> : ''}
                  {data.nearestFeatures?.majorAirport && data.nearestFeatures.majorAirport.faa_id !== data.nearestFeatures?.airport?.faa_id ? <>Major hub: <strong>{titleCase(data.nearestFeatures.majorAirport.name)}</strong> ({data.nearestFeatures.majorAirport.faa_id}) at {fmtMi(data.nearestFeatures.majorAirport.distance_miles)}. </> : ''}
                  {p.nearest_runway_5000ft_name && p.nearest_runway_5000ft_miles != null && p.nearest_runway_5000ft_name !== p.nearest_commercial_airport_name ? <>Closer corp-jet strip: <strong>{titleCase(p.nearest_runway_5000ft_name)}</strong>{p.nearest_runway_5000ft_length_ft ? ` (${p.nearest_runway_5000ft_length_ft.toLocaleString()} ft paved)` : ''} at {fmtMi(p.nearest_runway_5000ft_miles)}. </> : ''}
                  {p.nearest_urban_area_name && p.nearest_urban_area_miles != null ? <>Nearest urban area is <strong>{p.nearest_urban_area_name}</strong> at {fmtMi(p.nearest_urban_area_miles)} — labor draw available without attracting municipal-scale opposition.</> : ''}
                </>
              )}
            </p>
          </div>

          {/* Regulatory & environmental */}
          <div className="v2-infra-block">
            <div className="v2-infra-head">
              <h3>Regulatory &amp; environmental</h3>
              <span className={`v2-tag ${(p.te_species_count ?? 0) > 0 || p.in_critical_habitat ? 'amber' : ''}`}>
                {(p.te_species_count ?? 0) > 0 || p.in_critical_habitat ? 'Watch' : 'Clean'}
              </span>
            </div>
            <p>
              {p.in_nonattainment === false ? <><strong>Attainment</strong> for all seven NAAQS. </> : <><strong>Nonattainment</strong>{p.nonattainment_areas?.length ? ` for ${p.nonattainment_areas.map((a) => a.pollutant).join(', ')}` : ''}. </>}
              {isMeiners
                ? <><strong>TWDB 1% annual-chance screen</strong> (non-regulatory).</>
                : <><strong>FEMA Zone {p.flood_zone || 'X'}</strong>.</>}
              {p.ej_max_pctile != null ? <> <strong>EJ max percentile {p.ej_max_pctile}</strong> ({p.ej_max_pctile < 50 ? 'low' : p.ej_max_pctile < 80 ? 'moderate' : 'elevated'}).</> : ''}
              {p.seismic_pga != null ? <> Seismic PGA {Number(p.seismic_pga).toFixed(3)}g.</> : ''}
              {p.in_mine_subsidence_zone === true
                ? <> <strong>Parcel overlaps historic mine footprint</strong>{p.mine_overlap_acres ? ` (${Number(p.mine_overlap_acres).toFixed(0)} ac)` : ''} — subsurface assessment required.</>
                : p.nearest_mine_subsidence_miles != null
                  ? <> Nearest historic mine at <strong>{fmtMi(p.nearest_mine_subsidence_miles)}</strong> (outside parcel).</>
                  : ''}
              {(() => {
                const overlaps = p.aml_overlaps || [];
                if (overlaps.length === 0 || p.aml_overlap_acres == null || Number(p.aml_overlap_acres) <= 0) return null;
                const ac = Number(p.aml_overlap_acres);
                const parcelAc = p.acreage != null ? Number(p.acreage) : null;
                const pct = parcelAc ? Math.round((ac / parcelAc) * 100) : null;
                const priority = overlaps.filter(isPriorityAmlOverlap);
                const inventoriedOnly = priority.length === 0;
                const cleanAc = parcelAc != null ? Math.max(0, parcelAc - ac) : null;
                return (
                  <> <strong>WVDEP AML surface overlay:</strong> {pct != null ? `${pct}% of parcel (${Math.round(ac).toLocaleString()} ac)` : `${Math.round(ac).toLocaleString()} ac`} overlap pre-SMCRA highwall features — surface hazard, not underground workings.{' '}
                    {inventoriedOnly
                      ? <>All overlapping features are unfunded and predominantly <em>not on the federal AMLIS priority list</em>. Disclosure only; no foundation-collapse risk.</>
                      : <>{priority.length} feature{priority.length === 1 ? '' : 's'} with federally-listed or funded priority — request WVDEP file before LOI.</>}
                    {cleanAc != null && parcelAc ? <> ~{Math.round(cleanAc).toLocaleString()} ac ({Math.round((cleanAc / parcelAc) * 100)}%) of parcel unaffected and available for pad siting.</> : null}
                  </>
                );
              })()}
              {(p.te_species_count ?? 0) > 0
                ? <> <strong>{p.te_species_count}</strong> federal T&amp;E species range-overlap{p.te_species_count === 1 ? '' : 's'}; no critical habitat on site.</>
                : <> No T&amp;E overlaps.</>}
            </p>
          </div>

        </div>
      </section>

      {/* ============================================================= */}
      {/* EPISTEMIC STATUS                                                */}
      {/* ============================================================= */}
      <section>
        <Heading tag={hasPrecedent ? 'Sec. 07' : 'Sec. 06'} title="Diligence status" meta="What we know · what we infer · what we do not yet know" />

        <div className="v2-epist">

          {/* KNOWN — Site: things that define the parcel itself (size, terrain,
              regulatory designations). Direct measurements or authoritative
              registries. */}
          <div className="v2-epist-col" data-tier="known">
            <div className="v2-col-head">
              <h4>Known — Site</h4>
              <span className="v2-dot-mark" />
            </div>
            <ul>
              {useAggAcres
                ? <li>Assembly {data.carveOutAggregate?.slug === 'crownpine-blk-194-a1' ? Number(headlineAcres!).toLocaleString() : Math.round(headlineAcres!).toLocaleString()}&nbsp;ac<span className="v2-mono">{aggParcelCount} contiguous parcels, one owner</span></li>
                : (p.acreage != null && <li>Parcel {Number(p.acreage).toLocaleString()}&nbsp;ac<span className="v2-mono">tax record</span></li>)}
              {p.avg_slope_pct != null && <li>Avg slope {a1SlopeRange ? a1SlopeRange.avg : <>{Number(p.avg_slope_pct).toFixed(2)}%</>}<span className="v2-mono">{a1SlopeRange ? 'USGS 1 m lidar, 3–5 m grids' : 'USGS 3DEP 10m'}</span></li>}
              {p.flood_zone && <li>FEMA Zone {p.flood_zone}<span className="v2-mono">NFHL 2024</span></li>}
              {p.in_nonattainment === false && <li>Attainment, all NAAQS<span className="v2-mono">EPA Green Book</span></li>}
              {p.ej_max_pctile != null && <li>EJ max pctile {p.ej_max_pctile}<span className="v2-mono">EJScreen v2.3</span></li>}
              {p.seismic_pga != null && <li>Seismic PGA {Number(p.seismic_pga).toFixed(3)}g<span className="v2-mono">USGS NSHM 2023</span></li>}
            </ul>
          </div>

          {/* KNOWN — Infrastructure: what's around the parcel. Pipeline row is
              enriched with permit-verified capacity when pipeline_available_local_source
              starts with wvdep_permit (Goff, MVP, etc.). Former Believed items
              (utilization, MAOP) removed — 0% utilization was misleading and
              MAOP inference doesn't add memo-level value. */}
          <div className="v2-epist-col" data-tier="known">
            <div className="v2-col-head">
              <h4>Known — Infrastructure</h4>
              <span className="v2-dot-mark" />
            </div>
            <ul>
              {heroPipe && (() => {
                const permitId = p.pipeline_available_local_source?.startsWith('wvdep_permit')
                  ? p.pipeline_available_local_source.replace('wvdep_permit_', '')
                  : null;
                const pipeName = heroPipe.name && !/fullstream|holdings|llc|lp|inc|company|operating|corp/i.test(heroPipe.name)
                  ? titleCaseOperator(heroPipe.name).replace(/\s+pipeline\s*$/i, '')
                  : titleCaseOperator(heroPipe.operator || heroPipe.name);
                const attrs = [
                  isMeiners && meinersHeroCapacity
                    ? `${meinersHeroCapacity.label}: ${meinersHeroCapacity.value}`
                    : !isA1 && heroCap ? `${Math.round(heroCap).toLocaleString()} MMcf/d ${heroCapacity!.label.toLowerCase()}` : null,
                  heroPipe.diameter_inches ? `${heroPipe.diameter_inches}″` : null,
                ].filter(Boolean).join(' · ');
                return (
                  <li>
                    {pipeName} {heroOnSite ? 'crosses parcel' : fmtPipeDist(heroPipe.distance_miles)}{attrs ? ` · ${attrs}` : ''}
                    <span className="v2-mono">{permitId ? `WV DEP ${permitId} permit` : isA1 ? 'TX RRC + PostGIS' : isMeiners ? (meinersHeroCapacity?.sourceTitle || 'Operator confirmation required') : 'PostGIS / HIFLD'}</span>
                  </li>
                );
              })()}
              {p.nearest_substation_kv != null && <li>Substation {fmtMi(p.nearest_substation_miles)} / {p.nearest_substation_kv} kV<span className="v2-mono">HIFLD</span></li>}
              {/* Carrier-published route geometry (Segra/FiberLight/Zayo KMZ) + PostGIS distance —
                  same direct-source-plus-PostGIS standing as HIFLD rows above, not inference. */}
              {isA1
                ? <li>FiberLight backbone 0.57 mi<span className="v2-mono">assembly boundary to carrier route</span></li>
                : isMeiners
                  ? <li>FiberLight ~3.1 mi / Zayo ~6.3 mi<span className="v2-mono">whole-block boundary to carrier routes</span></li>
                  : p.nearest_carrier_fiber_name
                    ? <li>{p.nearest_carrier_fiber_name} backbone {fmtMi(p.nearest_carrier_fiber_miles)}<span className="v2-mono">parcel boundary to carrier route</span></li>
                    : null}
              {ixpOk && <li>{p.nearest_ixp_name} {fmtMi(p.nearest_ixp_miles)}<span className="v2-mono">PeeringDB IXP</span></li>}
              {coloOk && <li>{(p as any).nearest_colo_facility} {fmtMi((p as any).nearest_colo_miles)}<span className="v2-mono">PeeringDB colo (≥5 networks)</span></li>}
              {p.nearest_gas_plant_miles != null && !(data.gasInfrastructure?.compressors?.[0]?.distance_miles <= 1) && <li>Nearest gas plant {fmtMi(p.nearest_gas_plant_miles)}<span className="v2-mono">{p.state === 'TX' ? 'EIA-757 / RRC R-3' : 'EIA-757'}</span></li>}
              {p.nearest_highway_name && <li>{p.nearest_highway_name} {fmtMi(p.nearest_highway_miles)}<span className="v2-mono">HIFLD / FHWA</span></li>}
              {p.nearest_railroad_owner && <li>{humanizeRail(p.nearest_railroad_owner)} rail {fmtMi(p.nearest_railroad_miles)}<span className="v2-mono">HIFLD</span></li>}
              {p.nearest_commercial_airport_name && <li>{titleCase(p.nearest_commercial_airport_name)} Airport{isA1 ? ' (GGG)' : data.nearestFeatures?.airport?.faa_id ? ` (${data.nearestFeatures.airport.faa_id})` : ''} {fmtMi(p.nearest_commercial_airport_miles)}<span className="v2-mono">HIFLD FAA</span></li>}
              {data.nearestFeatures?.majorAirport && data.nearestFeatures.majorAirport.faa_id !== data.nearestFeatures?.airport?.faa_id && <li>{titleCase(data.nearestFeatures.majorAirport.name)} ({data.nearestFeatures.majorAirport.faa_id}) {fmtMi(data.nearestFeatures.majorAirport.distance_miles)}<span className="v2-mono">FAA NTAD major hub</span></li>}
              {p.nearest_runway_5000ft_name && p.nearest_runway_5000ft_miles != null && p.nearest_runway_5000ft_name !== p.nearest_commercial_airport_name && <li>{titleCase(p.nearest_runway_5000ft_name)} {p.nearest_runway_5000ft_length_ft ? `(${p.nearest_runway_5000ft_length_ft.toLocaleString()} ft paved) ` : ''}{fmtMi(p.nearest_runway_5000ft_miles)}<span className="v2-mono">FAA NTAD runways</span></li>}
            </ul>
          </div>

          <div className="v2-epist-col" data-tier="assumed">
            <div className="v2-col-head">
              <h4>Assumed</h4>
              <span className="v2-dot-mark" />
            </div>
            <ul>
              {isA1
                ? <li>TCEQ EGU Standard Permit path<span className="v2-mono">160 MW Phase 1 · controls equipment-specific</span></li>
                : <li>Standard PSD pathway<span className="v2-mono">at 160 MW/phase + SCR</span></li>}
              {isA1 ? (
                <>
                  <li>ET firm transport &amp; tariff terms<span className="v2-mono">supply confirmed; terms in negotiation</span></li>
                  <li>Tap/lateral feasibility on the western band<span className="v2-mono">customer pays for lateral; tap engineering to establish</span></li>
                </>
              ) : (
                <>
                  <li>Firm transport available on hero pipeline<span className="v2-mono">FERC tariff comparable</span></li>
                  <li>Tap grantable at reported distance<span className="v2-mono">customer pays for lateral</span></li>
                </>
              )}
              {gasPrecedent && <li>{gasPrecedent.name} envelope repeatable<span className="v2-mono">same airshed / regulator</span></li>}
            </ul>
          </div>

          <div className="v2-epist-col" data-tier="pending">
            <div className="v2-col-head">
              <h4>Pending</h4>
              <span className="v2-dot-mark" />
            </div>
            <ul>
              {isA1
                ? <li>Firm transport &amp; tap engineering with Energy Transfer<span className="v2-mono">supply confirmed; terms pending</span></li>
                : heroOnSite
                ? (ownerOwnsPipeline(p.owner_name, heroPipe)
                    ? <li>Tap construction &amp; pressure-regulation design<span className="v2-mono">owner-operated pipeline on parcel</span></li>
                    : <li>Operator interconnect study + firm-transport letter<span className="v2-mono">pipeline crosses parcel; tap not implied</span></li>)
                : <li>Firm transport letter from {heroPipe?.operator || 'pipeline operator'}<span className="v2-mono">interconnect study</span></li>}
              <li>Substation hosting capacity<span className="v2-mono">utility + queue position</span></li>
              <li>Enterprise carrier serviceability<span className="v2-mono">confirm capacity · construction · SLA · diversity</span></li>
              {p.water_service_area !== true && (
                hasWaterDisclosure
                  ? <li>Confirm landowner water specs<span className="v2-mono">operator materials + site visit</span></li>
                  : <li>Sustained water yield<span className="v2-mono">hydrogeological survey</span></li>
              )}
              {p.avg_slope_pct != null && Number(p.avg_slope_pct) > 8 && <li>Earthwork dollars per MW<span className="v2-mono">geotech + cut/fill bid</span></li>}
              {(p.te_species_count ?? 0) > 0 && (isA1 ? <li>T&E habitat assessment<span className="v2-mono">Section 7 only via federal nexus (§404)</span></li> : <li>USFWS Section 7 scoping<span className="v2-mono">informal consultation</span></li>)}
            </ul>
          </div>

        </div>

      </section>

      {/* ============================================================= */}
      {/* DECISION POINT                                                  */}
      {/* ============================================================= */}
      <section className="v2-decision">
        <div className="v2-eyebrow">
          DECISION POINT <span className="v2-dot" /> 60&nbsp;DAYS <span className="v2-dot" /> PRICED OR KILLED
        </div>
        <h2>What we need next.</h2>

        <ol>
          {data.gasSupplyZone ? (
            <li>
              <strong>{data.gasSupplyZone.operatorName} + {data.redacted === 'partner' ? 'developer' : 'Integra'} working meeting.</strong>{' '}
              Scope Phase 1 deliverability, gathering buildout terms, and the long-form gas supply agreement.
              Inside the AOI the firm path runs on operator gathering, so this is the timeline anchor;
              public pipeline interconnect stays in scope as the redundant fallback.
            </li>
          ) : (heroPipe && (
            isA1
              ? <li><strong>Energy Transfer firm transport &amp; tap engineering</strong> — gas availability and supply confirmed; commercial terms and tap scope anchor the timeline.</li>
              : heroOnSite
              ? (ownerOwnsPipeline(p.owner_name, heroPipe)
                  ? <li><strong>Tap + meter station timeline</strong> with {titleCaseOperator(heroPipe.operator || heroPipe.name)} — owner-operated pipeline on parcel; schedule tap construction and pressure-regulation design.</li>
                  : <li><strong>{titleCaseOperator(heroPipe.operator || heroPipe.name)} interconnect study</strong> — pipeline crosses parcel but does not imply an existing tap or receipt point; formal request within 14&nbsp;days anchors the timeline and the firm-transport question.</li>)
              : <li><strong>{titleCaseOperator(heroPipe.operator || heroPipe.name)} interconnect study</strong> — formal request within 14&nbsp;days; anchors the entire timeline.</li>
          ))}
          {p.avg_slope_pct != null && Number(p.avg_slope_pct) > 8 && <li><strong>Geotech and earthwork bid</strong> — 45 days; this is the only line item that can surprise the unit economics.</li>}
          {p.water_service_area !== true && (
            hasWaterDisclosure
              ? <li><strong>Confirm landowner-reported water specifications</strong> — operator materials + site visit, 1–2 weeks; source, yield, and quality to be documented.</li>
              : <li><strong>Hydrogeological survey</strong> — 30 days, concurrent; commercial-haul fallback priced in parallel.</li>
          )}
          {(p.te_species_count ?? 0) > 0 && (isA1 ? <li><strong>T&E habitat assessment</strong> — concurrent with geotech; Section 7 attaches only via a federal nexus (the §404 action, if the north drain is ruled jurisdictional).</li> : <li><strong>USFWS Section 7 scoping</strong> — informal consultation opened concurrent with geotech.</li>)}
          {/* A1 gets its own <li> rather than an inline ternary on the label: any
              expression inserted into the non-A1 text node changes React's SSR
              markers, which would break byte-identity on Goff and Paces. */}
          {isA1
            ? <li><strong>Title search &amp; terms draft</strong> to {p.owner_name ? `“${ownerDisplay(p.owner_name, 60)}”` : 'the current owner'} — concurrent; {aggParcelCount > 1 ? `single ownership entity across all ${aggParcelCount} assembly parcels; seller authority, transaction structure, title, exact carve-out, and definitive terms remain to be confirmed` : p.owner_parcel_count && p.owner_parcel_count > 1 ? `owner holds ${p.owner_parcel_count} parcels in the region, so a portfolio discussion may apply` : 'single-parcel signature path'}.</li>
            : <li><strong>Title search &amp; LOI draft</strong> to {p.owner_name ? `“${ownerDisplay(p.owner_name, 60)}”` : 'the current owner'} — concurrent; {p.owner_parcel_count && p.owner_parcel_count > 1 ? `owner holds ${p.owner_parcel_count} parcels in the region, so a portfolio discussion may apply` : 'single-parcel signature path'}.</li>}
        </ol>

        {isA1 ? (
          <p className="v2-closer">
            <strong>60 days from today,</strong> every open item on this brief has an accepted diligence path, owner, fee, and schedule.
            Nothing in this document prevents moving to terms this quarter.
          </p>
        ) : (
          <p className="v2-closer">
            <strong>60 days from today,</strong> every open item on this brief is priced or killed.
            Nothing in this document prevents an LOI this quarter.
          </p>
        )}
      </section>

      {/* ============================================================= */}
      {/* FOOTER                                                          */}
      {/* ============================================================= */}
      <footer className="v2-footer">
        <div className="v2-brand">
          <img src="/atwater-logo-dark.svg" alt="Atwater Energy" className="v2-brand-logo" />
          <div className="v2-mono">Pre-feasibility screening · Brief v2 · Data-as-of {data.freshness?.data_as_of || todayStr}</div>
        </div>
        <div className="v2-sources">
          {p.state === 'TX' ? 'TX RRC, ' : ''}EIA · PHMSA · {isMeiners ? 'TWDB 2024 State Flood Plan · non-regulatory 1% screen' : 'FEMA NFHL'} · USGS&nbsp;3DEP · EPA Green&nbsp;Book · EJScreen v2.3
          &middot; HIFLD · FCC BDC · PeeringDB · USFWS IPaC · FERC · Atwater overlays
          {(() => {
            // A1: gated off, same as the v1 chip. The only line in the table is
            // intrastate HPL/Texoma, which posts nothing on the ETP EBB — naming the
            // portal implies a utilization source that never existed for this corridor.
            if (isA1) return null;
            const ops = (data.pipelines || []).map(p => `${p.operator || ''} ${p.name || ''}`.toLowerCase()).join(' | ');
            const portals: string[] = [];
            if (/kinder morgan|\btgp\b|tennessee gas|\bngpl\b|el paso natural gas|\bepng\b/i.test(ops)) portals.push('NGPL/TGP/EPNG (KM) pipeline2.kindermorgan.com');
            if (/gulf south|boardwalk|\bbwp\b/i.test(ops)) portals.push('Gulf South (BWP) reporting.prod.bwpmlp.org');
            if (/texas eastern|tetco|enbridge|spectra/i.test(ops)) portals.push('Texas Eastern (Enbridge) rtba.enbridge.com');
            if (/energy transfer|\betp\b|panhandle eastern|trunkline/i.test(ops)) portals.push('Energy Transfer ebb.energytransfer.com');
            if (portals.length === 0) return null;
            return <> · EBB portals: {portals.join(' · ')}</>;
          })()}
        </div>
      </footer>
    </div>
  );
};

// ---------- helpers ----------------------------------------------------------

function humanizeRail(abbr: string | null | undefined): string {
  const map: Record<string, string> = { CSXT: 'CSX Transportation', BNSF: 'BNSF Railway', UP: 'Union Pacific Railroad', NS: 'Norfolk Southern Railway', CN: 'Canadian National Railway', CP: 'Canadian Pacific Railway' };
  if (!abbr) return 'Rail';
  return map[abbr.toUpperCase()] || abbr;
}

const PRESERVE_UPPER = new Set(['LLC', 'LP', 'INC', 'CO', 'LTD', 'DCP', 'NGL', 'USA', 'US', 'TX', 'WV', 'NM', 'AZ', 'OK', 'LA', 'PA', 'OH', 'VA', 'II', 'III', 'IV', 'GCSTREAM']);
export function titleCaseOperator(s: string | null | undefined): string {
  if (!s) return '';
  // Split trailing abbreviations that are concatenated (e.g. "OPERATINGLLC" → "Operating LLC")
  const normalized = s.replace(/(?<=[a-zA-Z])(LLC|LP|INC|LTD)(?=\s|[.,]|$)/gi, ' $1');
  return normalized.split(/\s+/).map((w) => {
    const bare = w.replace(/[.,]$/g, '');
    if (PRESERVE_UPPER.has(bare.toUpperCase())) return bare.toUpperCase() + w.slice(bare.length);
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join(' ');
}

// Owner display: title-case and optionally truncate. Used wherever owner_name is rendered.
function ownerDisplay(s: string | null | undefined, maxLen?: number): string {
  const titled = titleCaseOperator(s);
  return maxLen ? truncate(titled, maxLen) : titled;
}

function titleCase(s: string): string {
  return s.split(/\s+/).map(w => w.split('/').map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('/')).join(' ').replace(/\bWv\b/g, 'WV');
}

const ON_SITE_THRESHOLD = 0.05;
function fmtPipeDist(d: number | string | null | undefined): string {
  if (d == null) return '—';
  const n = Number(d);
  if (n <= ON_SITE_THRESHOLD) return 'crosses parcel';
  return `${n.toFixed(2)} mi`;
}

function stateName(abbr: string): string {
  const map: Record<string, string> = {
    TX: 'Texas', WV: 'West Virginia', NM: 'New Mexico', OK: 'Oklahoma',
    AZ: 'Arizona', LA: 'Louisiana', PA: 'Pennsylvania', OH: 'Ohio', VA: 'Virginia',
  };
  return map[abbr] || abbr;
}

function truncate(s: string, max: number): string {
  if (!s) return '';
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

// `range` (Crown Pine A1) replaces the single figures with the 3–5 m grid range; the
// wording threshold still reads `avg`, the 3 m end.
function slopeConstraintText(avg: number | null, pctUnder5: number | null, landReadiness?: string | null, range?: { avg: string; under5: string } | null): JSX.Element | string {
  if (avg == null) return <>Terrain data is unavailable — earthwork costs require a geotech assessment.</>;
  const avgN = Number(avg);
  const ucN = pctUnder5 != null ? Number(pctUnder5) : null;
  const mitigation = landReadiness ? <> <em>Landowner has committed to earthwork and site preparation for qualified buyers.</em></> : null;
  if (range) {
    if (avgN < 3) return <>The site is flat (avg slope <strong>{range.avg}</strong>) — earthwork is not a gating constraint.</>;
    if (avgN < 8) return <>Terrain is moderate (avg slope <strong>{range.avg}</strong>, <strong>{range.under5}</strong> of the analyzed core under 5% grade) — earthwork is manageable at BTM footprint scale.{mitigation}</>;
    return <>The primary physical constraint is terrain: avg slope <strong>{range.avg}</strong>, and only <strong>{range.under5}</strong> of the analyzed core sits below 5% grade. The campus footprint requires deliberate cut/fill or pad selection from a geotech study.{mitigation}</>;
  }
  if (avgN < 3) return <>The site is flat (avg slope <strong>{avgN.toFixed(1)}%</strong>) — earthwork is not a gating constraint.</>;
  if (avgN < 8) return <>Terrain is moderate (avg slope <strong>{avgN.toFixed(1)}%</strong>{ucN != null ? <>, <strong>{ucN.toFixed(1)}%</strong> of parcel under 5% grade</> : ''}) — earthwork is manageable at BTM footprint scale.{mitigation}</>;
  return <>The primary physical constraint is terrain: avg slope <strong>{avgN.toFixed(1)}%</strong>{ucN != null ? <>, and only <strong>{ucN.toFixed(1)}%</strong> of parcel sits below 5% grade</> : ''}. The campus footprint requires deliberate cut/fill or pad selection from a geotech study.{mitigation}</>;
}
